import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import EditableEmail from '../components/EditableEmail';
import { clientsAPI } from '../services/api';
import type { Client } from '../types';
import { formatLocalDateOnly, formatLocalDateTime } from '../utils/timezone';
import { Trash2, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from 'react-aria-components';

interface ClientStats {
  total_clients: number;
  total_bookings: number;
  clients_with_multiple_bookings: number;
  top_clients: Client[];
}

type SortField = 'last_form_submission_date' | 'created_at' | null;
type SortDirection = 'asc' | 'desc';

const ClientsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params on mount
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialPageSize = parseInt(searchParams.get('page_size') || '25', 10);
  const initialSearch = searchParams.get('search') || '';
  
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('last_form_submission_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchInput, setSearchInput] = useState(initialSearch); // Input value (updates immediately)
  const [searchTerm, setSearchTerm] = useState(initialSearch); // Actual search term (updates with debounce)
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  
  // Update URL when state changes
  const updateURL = useCallback((updates: { page?: number; page_size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.page !== undefined) {
      if (updates.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', updates.page.toString());
      }
    }
    
    if (updates.page_size !== undefined) {
      if (updates.page_size === 25) {
        newParams.delete('page_size');
      } else {
        newParams.set('page_size', updates.page_size.toString());
      }
    }
    
    if (updates.search !== undefined) {
      if (updates.search === '') {
        newParams.delete('search');
      } else {
        newParams.set('search', updates.search);
      }
    }
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        page_size: pageSize,
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (sortField) {
        const ordering = sortDirection === 'asc' ? sortField : `-${sortField}`;
        params.ordering = ordering;
      }
      
      const response = await clientsAPI.getAll(params);
      console.log('Clients response:', response);
      console.log('Request params:', params);
      
      // Handle paginated response from Django REST Framework
      if (response && typeof response === 'object') {
        if (Array.isArray(response.results)) {
          // Paginated response format: { count, next, previous, results }
          setClients(response.results);
          setTotalCount(response.count || 0);
          console.log(`Loaded ${response.results.length} clients, total count: ${response.count}, page: ${currentPage}`);
        } else if (Array.isArray(response)) {
          // Fallback for non-paginated response (shouldn't happen with pagination enabled)
          setClients(response);
          setTotalCount(response.length);
          console.log(`Loaded ${response.length} clients (non-paginated), page: ${currentPage}`);
        } else {
          setClients([]);
          setTotalCount(0);
          console.log('Unexpected response format:', response);
        }
      } else {
        setClients([]);
        setTotalCount(0);
        console.log('No clients in response or invalid format');
      }
    } catch (err: any) {
      console.error('Failed to load clients:', err);
      setClients([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, sortField, sortDirection]);

  // Sync state with URL params when they change externally (e.g., browser back/forward or direct URL)
  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page') || '1', 10);
    const urlPageSize = parseInt(searchParams.get('page_size') || '25', 10);
    const urlSearch = searchParams.get('search') || '';
    
    // Only update if values are different to avoid infinite loops
    if (urlPage !== currentPage && urlPage >= 1) {
      console.log(`URL page changed: ${currentPage} -> ${urlPage}`);
      setCurrentPage(urlPage);
    }
    if (urlPageSize !== pageSize && [25, 50, 100].includes(urlPageSize)) {
      console.log(`URL page size changed: ${pageSize} -> ${urlPageSize}`);
      setPageSize(urlPageSize);
    }
    if (urlSearch !== searchTerm) {
      console.log(`URL search changed: "${searchTerm}" -> "${urlSearch}"`);
      setSearchTerm(urlSearch);
      setSearchInput(urlSearch); // Also update input value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Load clients when dependencies change
  useEffect(() => {
    console.log(`Loading clients: page=${currentPage}, pageSize=${pageSize}, search="${searchTerm}"`);
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, sortField, sortDirection]);

  // Prevent duplicate stats loading on initial mount
  const statsLoadedRef = React.useRef(false);

  useEffect(() => {
    if (!statsLoadedRef.current) {
      statsLoadedRef.current = true;
      loadStats();
    }
  }, []);

  const loadStats = async () => {
    try {
      const statsData = await clientsAPI.getStats();
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleEmailUpdate = async (clientId: number, newEmail: string) => {
    await clientsAPI.updateEmail(clientId, newEmail);
    // Reload clients to reflect the change
    await loadClients();
  };

  const handleDelete = async (clientId: number) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      return;
    }
    
    try {
      await clientsAPI.delete(clientId);
      await loadClients();
      alert('Client deleted successfully');
    } catch (err: any) {
      console.error('Failed to delete client:', err);
      alert('Failed to delete: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
    const newPage = 1;
    setCurrentPage(newPage);
    updateURL({ page: newPage });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value); // Update input immediately for better UX
  };

  // Debounce search term - update after user stops typing for 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        const newPage = 1;
        setCurrentPage(newPage);
        updateURL({ page: newPage, search: searchInput });
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    const newPage = 1;
    setCurrentPage(newPage);
    updateURL({ page: newPage, page_size: newSize });
  };

  const handlePageChange = (newPage: number) => {
    console.log(`Page change requested: ${currentPage} -> ${newPage}`);
    setCurrentPage(newPage);
    updateURL({ page: newPage });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const PaginationComponent = () => {
    // Always show pagination controls if there are any clients, even if only one page

    const getPageNumbers = () => {
      const pages: (number | string)[] = [];
      const maxVisible = 7;
      
      if (totalPages <= maxVisible) {
        // Show all pages
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show first page, last page, current page, and ellipsis
        pages.push(1);
        
        if (currentPage > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (currentPage < totalPages - 2) {
          pages.push('...');
        }
        
        pages.push(totalPages);
      }
      
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-700">
            {totalCount > 0 ? (
              <>Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} clients</>
            ) : (
              <>No clients found</>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none bg-white"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-600">per page</span>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-1">
              {getPageNumbers().map((page, index) => {
                if (page === '...') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                      ...
                    </span>
                  );
                }
                
                const pageNum = page as number;
                const isActive = pageNum === currentPage;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded-md transition-colors ${
                      isActive
                        ? 'bg-accent-500 text-white font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Check if user has access
  if (user && !user.can_view_all_tasks) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center">
              <h1 className="text-2xl font-bold text-red-900 mb-4">⛔ Access Denied</h1>
              <p className="text-red-800">Only Owners and Administrators can view clients.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
            <p className="mt-4 text-gray-600">Loading clients...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">👥 Clients</h1>
            <p className="mt-2 text-gray-600">
              Summary of all clients who have booked appointments
            </p>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Total Clients */}
              <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-primary-100 text-sm font-medium">Total Clients</p>
                    <p className="text-3xl font-bold mt-2">{stats.total_clients}</p>
                  </div>
                  <div className="bg-primary-400 bg-opacity-30 rounded-full p-3">
                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Repeat Clients */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm font-medium">Repeat Clients</p>
                    <p className="text-3xl font-bold mt-2">{stats.clients_with_multiple_bookings}</p>
                  </div>
                  <div className="bg-purple-400 bg-opacity-30 rounded-full p-3">
                    <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Clients Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">All Clients</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {totalCount > 0 ? (
                      <>Showing {clients.length} of {totalCount} client{totalCount !== 1 ? 's' : ''}</>
                    ) : (
                      <>No clients found</>
                    )}
                  </p>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchInput}
                    onChange={(e) => handleSearchInputChange(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 outline-none"
                  />
                </div>
              </div>
              
              {/* Pagination Top */}
              {totalCount > 0 && <PaginationComponent />}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Assigned Manager
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('last_form_submission_date')}
                        className="flex items-center justify-center gap-1 hover:text-gray-900 transition-colors"
                      >
                      Last Submission
                        {sortField === 'last_form_submission_date' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )
                        ) : (
                          <div className="w-4 h-4 opacity-30">
                            <ArrowUp className="w-4 h-4" />
                          </div>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('created_at')}
                        className="flex items-center justify-center gap-1 hover:text-gray-900 transition-colors"
                      >
                      Client Since
                        {sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )
                        ) : (
                          <div className="w-4 h-4 opacity-30">
                            <ArrowUp className="w-4 h-4" />
                          </div>
                        )}
                      </button>
                    </th>
                    {user?.role === 'owner' && (
                      <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clients.length === 0 ? (
                    <tr>
                      <td colSpan={user?.role === 'owner' ? 7 : 6} className="px-6 py-12 text-center text-gray-500">
                        {searchTerm ? 'No clients found matching your search' : 'No clients found'}
                      </td>
                    </tr>
                  ) : (
                    clients.map((client) => {
                      const fullName = client.full_name || `${client.first_name} ${client.last_name}`.trim();
                      const initials = `${client.first_name?.[0] || ''}${client.last_name?.[0] || ''}`.toUpperCase();
                      return (
                        <tr key={client.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-accent-500 rounded-full flex items-center justify-center text-white font-semibold">
                                {initials || '?'}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-gray-900">
                                  {fullName || 'N/A'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <EditableEmail
                              email={client.email}
                              onUpdate={(newEmail) => handleEmailUpdate(client.id, newEmail)}
                              clientId={client.id}
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {client.phone_number || <span className="text-gray-400">—</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {client.assigned_manager_email ? (
                                <div className="flex flex-col">
                                  <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium mb-1">
                                    {client.assigned_manager_name || 'Manager'}
                                  </span>
                                  <span className="text-xs text-gray-600">
                                    {client.assigned_manager_email}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-900">
                              {client.last_form_submission_date
                                ? formatLocalDateTime(client.last_form_submission_date)
                                : <span className="text-gray-400">—</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <div className="text-sm text-gray-600">
                              {formatLocalDateOnly(client.created_at)}
                            </div>
                          </td>
                          {user?.role === 'owner' && (
                            <td className="px-6 py-4 text-center">
                              <Button
                                onPress={() => handleDelete(client.id)}
                                className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors mx-auto"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </Button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Bottom */}
            {totalCount > 0 && <PaginationComponent />}
          </div>

        </div>
      </div>
    </>
  );
};

export default ClientsPage;

